/* NSE cart add-ons — Lens Care Kit row + Gift Wrap toggle (markup: snippets/nse-cart-addons.liquid).
   Kit: Add ⇄ "Added ✓" (the line carries a hidden `_addon` property; a kit added elsewhere hides the row).
   Gift wrap: on adds 1, off removes it and clears the cart note; max 1 per order, and it is removed when
   nothing else is left. Re-renders the cart sections exactly like Dawn's cart.js (via the context's
   <cart-drawer-items>/<cart-items> getSectionsToRender), so totals update in place. */
if (!customElements.get('nse-cart-addons')) {
  class NseCartAddons extends HTMLElement {
    /* shared across the drawer + cart-page instances so they never fire overlapping cart writes */
    static busy = false;

    connectedCallback() {
      this.wrap = this.querySelector('[data-addons-wrap]');
      this.error = this.querySelector('[data-addons-error]');
      this.kitRow = this.querySelector('[data-kit-row]');
      this.kitBtn = this.querySelector('[data-kit-btn]');
      this.giftToggle = this.querySelector('[data-gift-toggle]');
      this.giftPanel = this.querySelector('[data-gift-panel]');
      this.note = this.querySelector('[data-gift-note]');

      if (this.kitBtn) this.kitBtn.addEventListener('click', () => this.onKit());
      if (this.giftToggle) this.giftToggle.addEventListener('change', () => this.onGift());
      if (this.note) {
        this.lastNote = this.note.value;
        const debouncedSave = debounce(() => this.saveNote(), 500);
        this.note.addEventListener('input', () => {
          this.autosize();
          debouncedSave();
        });
        this.note.addEventListener('change', () => this.saveNote());
        this.autosize();
      }

      /* any cart change (quantity edits, removals, product-page adds) → resync + re-apply the rules */
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());

      this.enforce({
        giftKey: this.dataset.giftKey,
        giftQty: Number(this.dataset.giftQty) || 0,
        others: Number(this.dataset.others) || 0,
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    cartItems() {
      return document.querySelector(this.dataset.context === 'drawer' ? 'cart-drawer-items' : 'cart-items');
    }

    onKit() {
      const key = this.dataset.kitKey;
      if (key) {
        this.request(routes.cart_update_url, { updates: { [key]: 0 } });
      } else {
        this.request(routes.cart_add_url, {
          items: [{ id: Number(this.dataset.kitVariantId), quantity: 1, properties: { _addon: 'cart' } }],
        });
      }
    }

    onGift() {
      const on = this.giftToggle.checked;
      this.giftPanel.hidden = !on;
      if (on) {
        this.request(routes.cart_add_url, { items: [{ id: Number(this.dataset.giftVariantId), quantity: 1 }] }, false);
      } else if (this.dataset.giftKey) {
        this.request(routes.cart_update_url, { updates: { [this.dataset.giftKey]: 0 }, note: '' }, true);
      }
    }

    enforce(state) {
      if (NseCartAddons.busy || !state.giftKey) return;
      if (state.others === 0) {
        this.request(routes.cart_update_url, { updates: { [state.giftKey]: 0 }, note: '' });
      } else if (state.giftQty > 1) {
        this.request(routes.cart_update_url, { updates: { [state.giftKey]: 1 } });
      }
    }

    async refresh() {
      const cart = await CartItems.fetchCartData();
      if (!cart || !this.isConnected) return;

      const { kitHandle, giftHandle } = this.dataset;
      let gift = null;
      let kitAddon = null;
      let kitElsewhere = false;
      let main = 0;
      let others = 0;
      cart.items.forEach((item) => {
        if (item.handle === giftHandle) {
          gift = item;
          return;
        }
        others += item.quantity;
        if (item.handle !== kitHandle) main += item.quantity;
        else if (item.properties && item.properties._addon) kitAddon = item;
        else kitElsewhere = true;
      });

      this.wrap.hidden = main === 0;
      if (this.kitRow) {
        this.kitRow.hidden = kitElsewhere;
        this.setKit(kitAddon ? kitAddon.key : '');
      }
      if (this.giftToggle) {
        this.dataset.giftKey = gift ? gift.key : '';
        this.setGift(Boolean(gift));
        if (document.activeElement !== this.note) {
          this.note.value = this.lastNote = cart.note || '';
          this.autosize();
        }
      }
      this.enforce({ giftKey: gift ? gift.key : '', giftQty: gift ? gift.quantity : 0, others });
    }

    setKit(key) {
      this.dataset.kitKey = key;
      const title = this.kitRow.querySelector('.nse-ao__name').textContent;
      this.kitBtn.textContent = key ? 'Added ✓' : 'Add';
      this.kitBtn.classList.toggle('is-added', Boolean(key));
      this.kitBtn.setAttribute('aria-pressed', String(Boolean(key)));
      this.kitBtn.setAttribute('aria-label', `${key ? 'Remove' : 'Add'} ${title}`);
    }

    setGift(on) {
      this.giftToggle.checked = on;
      this.giftPanel.hidden = !on;
      if (on) this.autosize();
    }

    async request(url, payload, giftRevertTo) {
      if (NseCartAddons.busy) {
        if (giftRevertTo !== undefined) this.setGift(giftRevertTo);
        return;
      }
      NseCartAddons.busy = true;
      this.setLoading(true);
      this.error.hidden = true;

      const cartItems = this.cartItems();
      const sections = cartItems ? cartItems.getSectionsToRender() : [];
      const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';

      try {
        const response = await fetch(url, {
          ...fetchConfig(),
          body: JSON.stringify({
            ...payload,
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status || state.errors) throw new Error(state.description || state.errors);

        sections.forEach((section) => {
          const host = document.getElementById(section.id);
          if (!host || !state.sections || !state.sections[section.section]) return;
          const target = host.querySelector(section.selector) || host;
          target.innerHTML = cartItems.getSectionInnerHTML(state.sections[section.section], section.selector);
        });
        if (typeof payload.note === 'string') this.lastNote = payload.note;

        NseCartAddons.busy = false;
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nse-cart-addons', cartData: state });
        /* the section re-render swapped this element out — keep keyboard focus on the new control */
        if (focusId && !this.isConnected) document.getElementById(focusId)?.focus();
      } catch (e) {
        console.error(e);
        if (giftRevertTo !== undefined) this.setGift(giftRevertTo);
        this.error.hidden = false;
      } finally {
        NseCartAddons.busy = false;
        this.setLoading(false);
      }
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
      if (this.kitBtn) this.kitBtn.disabled = on;
      if (this.giftToggle) this.giftToggle.disabled = on;
      /* hold checkout until the cart write lands, so Razorpay / checkout always reads the final cart.
         Only re-enable the exact button we disabled — a re-rendered one already has the server's state. */
      if (on) {
        this.heldCheckout = document.getElementById(this.dataset.checkoutId);
        if (this.heldCheckout && !this.heldCheckout.disabled) this.heldCheckout.disabled = true;
        else this.heldCheckout = null;
      } else if (this.heldCheckout) {
        this.heldCheckout.disabled = false;
        this.heldCheckout = null;
      }
    }

    autosize() {
      if (!this.note || this.giftPanel.hidden) return;
      this.note.style.height = 'auto';
      this.note.style.height = `${this.note.scrollHeight + 2}px`;
    }

    saveNote() {
      const value = this.note.value;
      if (value === this.lastNote) return;
      this.lastNote = value;
      fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ note: value }) }).catch((e) =>
        console.error(e)
      );
    }
  }

  customElements.define('nse-cart-addons', NseCartAddons);
}
